import { Button, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { SECURITY_ACTIONS } from '../../../common/auth/Security';
import type { SecuredActionType } from '../../../common/components/ActionType';
import ResponsiveActions from '../../../common/components/ResponsiveActions';
import { ENTITY_CONSTANTS } from '../../../common/constants/entityConstants';
import I18n from '../../../common/i18n/I18n';
import { Icons } from '../../../common/icons/Icons';
import { H, V } from '../../../common/Layout';
import { useStyling } from '../../../common/Theme';
import { BASIC_INFO_FORM_TYPE } from '../editComponents/BasicInfoFormUtils';

type AboutSectionProps = {
    about: string;
    editEnabled?: boolean;
    allowedEdit?: boolean;
    onEditPerson?: (editFieldName: string) => void;
};

export function AboutSection({ about, editEnabled, allowedEdit, onEditPerson }: AboutSectionProps) {
    const { theme, isMobile } = useStyling();

    const [editAbout, setEditAbout] = useState(false);

    const securedActions = useMemo<SecuredActionType[]>(
        () => [
            {
                securityAction: SECURITY_ACTIONS.EDIT,
                entityName: ENTITY_CONSTANTS.person,
                label: <I18n token="form.title.edit" />,
                icon: Icons.Edit,
                onClick: () => {
                    onEditPerson?.(BASIC_INFO_FORM_TYPE.about);
                }
            }
        ],
        [onEditPerson]
    );

    return (
        <V gap={1.5} onMouseEnter={() => setEditAbout(true)} onMouseLeave={() => setEditAbout(false)}>
            <H sx={{ height: isMobile ? '55px' : '35px', justifyContent: isMobile && 'space-between' }}>
                <Typography fontSize={20} color={theme.palette.text.secondary} lineHeight={1.2}>
                    <I18n token="person.about.title" />
                </Typography>
                {allowedEdit && editEnabled && about && (
                    <ResponsiveActions
                        isActivated={editAbout}
                        securedActions={securedActions}
                        containerSx={{ p: 0 }}
                    />
                )}
            </H>

            {about ? (
                <H style={{ overflow: 'auto' }}>
                    <Typography sx={{ whiteSpace: 'pre-wrap', height: '100px' }} lineHeight={1.2}>
                        {about}
                    </Typography>
                </H>
            ) : allowedEdit ? (
                <V sx={{ height: { md: 'fit-content', lg: '100px' }, justifyContent: 'center', alignItems: 'center' }}>
                    <Button
                        variant="outlined"
                        startIcon={<Icons.AddOutlined />}
                        sx={{ textTransform: 'capitalize' }}
                        onClick={() => {
                            onEditPerson(BASIC_INFO_FORM_TYPE.about);
                        }}
                    >
                        <I18n token="person.add.about.button" />
                    </Button>
                </V>
            ) : null}
        </V>
    );
}
